import React, { useState, useEffect, useCallback } from "react";
import { useAuthContext } from "../contexts/AuthContext";
import { authenticatedFetch } from "../utils/authenticatedFetch";
import styles from "./AdminSupervisors.module.css";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SupervisorSummary {
    user_id: string;
    email: string;
    full_name: string;
    max_seats: number;
    active_students: number;
    pending_invitations: number;
    seats_used: number;
}

interface SupervisorDetail {
    user_id: string;
    profile: { email: string; full_name: string; created_at?: string };
    settings: { is_supervisor: boolean; max_seats: number };
    seats: { used: number; max: number; active_students: number; pending_invitations: number };
    students: StudentRow[];
    invitations: InvitationRow[];
}

interface StudentRow {
    student_id: string;
    email: string;
    full_name: string;
    joined_at: string;
    status: "active" | "unlinked";
    unlinked_at: string | null;
    has_supervisor_granted_subscription: boolean;
}

interface SupervisorActivitySummary {
    linked_students: number;
    students_active: number;
    students_inactive: number;
    ai_interactions: number;
    chat_messages: number;
    direct_questions: number;
    qbank_reveals: number;
    mock_exams_completed: number;
    avg_mock_score_pct: number;
}

interface StudentActivity {
    student_id: string;
    email: string;
    full_name: string;
    joined_at: string;
    last_active_at: string | null;
    active_this_week: boolean;
    ai_interactions_this_week: number;
    chat_messages_this_week: number;
    direct_questions_this_week: number;
    qbank_reveals_this_week: number;
    days_active_this_week: number;
    mock_exams_completed_this_week: number;
    avg_mock_score_pct_this_week: number;
    qbank_progress_pct: number;
    since_linked: {
        active: boolean;
        last_active_at: string | null;
        ai_interactions: number;
        chat_messages: number;
        direct_questions: number;
        qbank_reveals: number;
        days_active: number;
        mock_exams_completed: number;
        avg_mock_score_pct: number;
    };
}

interface SupervisorActivity {
    window: "7d";
    period_start: string;
    period_end: string;
    summary: SupervisorActivitySummary;
    since_linked: {
        scope: "current_relationship";
        period_end: string;
        summary: SupervisorActivitySummary;
    };
    students: StudentActivity[];
}

interface InvitationRow {
    id: string;
    email: string;
    status: string;
    invited_at: string;
    accepted_at: string | null;
    expires_at: string | null;
}

type View = "list" | "detail" | "promote";

// ---------------------------------------------------------------------------
// Shared inline styles
// ---------------------------------------------------------------------------

const card: React.CSSProperties = {
    background: "var(--theme-surface-strong, var(--Colors-Neutral-000))",
    border: "1px solid var(--theme-border-strong, var(--Colors-Neutral-200))",
    borderRadius: "16px",
    padding: "1.5rem",
    marginBottom: "1.5rem",
    boxShadow: "0 2px 12px var(--theme-shadow, rgba(32,91,90,0.03))",
};

const btn: React.CSSProperties = {
    padding: "0.5rem 1rem",
    border: "none",
    borderRadius: "10px",
    fontSize: "0.875rem",
    fontWeight: 600,
    cursor: "pointer",
};

const primaryBtn: React.CSSProperties = {
    ...btn,
    background: "var(--theme-primary-action, var(--Colors-Primary-700))",
    color: "var(--theme-on-primary, white)",
};

const dangerBtn: React.CSSProperties = {
    ...btn,
    background: "var(--Colors-Accent-600, #9f4141)",
    color: "var(--Colors-Neutral-000, white)",
};

const ghostBtn: React.CSSProperties = {
    ...btn,
    background: "transparent",
    color: "var(--theme-control-text, var(--Colors-Primary-700))",
    border: "1px solid var(--theme-control-border, var(--Colors-Primary-600))",
};

const dangerGhostBtn: React.CSSProperties = {
    ...btn,
    background: "transparent",
    color: "var(--theme-danger-text, #dc3545)",
    border: "1px solid var(--theme-danger-text, #dc3545)",
};

const th: React.CSSProperties = {
    textAlign: "left",
    padding: "0.625rem 1rem",
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "1px",
    color: "var(--Colors-Neutral-500)",
    background: "var(--Colors-Neutral-101)",
    borderBottom: "1px solid var(--Colors-Neutral-200)",
};

const td: React.CSSProperties = {
    padding: "0.75rem 1rem",
    fontSize: "0.875rem",
    color: "var(--Colors-Neutral-700)",
    borderBottom: "1px solid var(--Colors-Neutral-100)",
};

const badge = (status: string): React.CSSProperties => {
    const colors: Record<string, { bg: string; fg: string }> = {
        active: { bg: "var(--theme-success-bg)", fg: "var(--theme-success-text)" },
        pending: { bg: "var(--theme-warning-bg)", fg: "var(--theme-warning-text)" },
        accepted: { bg: "var(--theme-success-bg)", fg: "var(--theme-success-text)" },
        expired: { bg: "var(--theme-danger-bg)", fg: "var(--theme-danger-text)" },
        cancelled: { bg: "var(--Colors-Neutral-200)", fg: "var(--Colors-Neutral-600)" },
        unlinked: { bg: "var(--theme-danger-bg)", fg: "var(--theme-danger-text)" },
    };
    const c = colors[status] || colors.pending;
    return {
        display: "inline-block",
        padding: "0.2rem 0.5rem",
        borderRadius: "6px",
        fontSize: "0.75rem",
        fontWeight: 600,
        textTransform: "capitalize",
        background: c.bg,
        color: c.fg,
    };
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AdminSupervisors: React.FC = () => {
    const { user } = useAuthContext();

    const [view, setView] = useState<View>("list");

    // List view
    const [supervisors, setSupervisors] = useState<SupervisorSummary[]>([]);
    const [listLoading, setListLoading] = useState(true);

    // Detail view
    const [detail, setDetail] = useState<SupervisorDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [activity, setActivity] = useState<SupervisorActivity | null>(null);
    const [activityLoading, setActivityLoading] = useState(false);
    const [activityError, setActivityError] = useState("");

    // Promote view
    const [lookupEmail, setLookupEmail] = useState("");
    const [lookupResult, setLookupResult] = useState<{
        user_id: string; email: string; full_name: string;
        is_supervisor: boolean; supervisor_max_seats: number;
    } | null>(null);
    const [lookupError, setLookupError] = useState("");
    const [lookupLoading, setLookupLoading] = useState(false);
    const [isSupervisor, setIsSupervisor] = useState(false);
    const [maxSeats, setMaxSeats] = useState(20);
    const [saving, setSaving] = useState(false);

    // Action loading states
    const [actionLoading, setActionLoading] = useState<string | null>(null);

    // Edit settings in detail view
    const [editMaxSeats, setEditMaxSeats] = useState(0);
    const [editingSupervisorSettings, setEditingSupervisorSettings] = useState(false);

    // Confirmation modals
    const [confirmAction, setConfirmAction] = useState<{
        title: string; message: string; action: () => Promise<void>;
    } | null>(null);

    // -----------------------------------------------------------------------
    // Data fetching
    // -----------------------------------------------------------------------

    const fetchSupervisors = useCallback(async () => {
        if (!user?.id) return;
        setListLoading(true);
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/admin/supervisors?user_id=${user.id}`);
            if (res.ok) setSupervisors(await res.json());
        } catch (err) {
            console.error("Failed to load supervisors", err);
        } finally {
            setListLoading(false);
        }
    }, [user?.id]);

    useEffect(() => {
        fetchSupervisors();
    }, [fetchSupervisors]);

    const fetchActivity = useCallback(async (supervisorId: string) => {
        if (!user?.id) return;
        setActivityLoading(true);
        setActivityError("");
        try {
            const res = await authenticatedFetch(
                `${apiUrl}/api/admin/supervisors/${supervisorId}/activity?user_id=${user.id}&window=7d`
            );
            if (res.ok) {
                setActivity(await res.json());
            } else {
                const data = await res.json();
                setActivityError(data.error || "Failed to load activity");
                setActivity(null);
            }
        } catch {
            setActivityError("Failed to load activity");
            setActivity(null);
        } finally {
            setActivityLoading(false);
        }
    }, [user?.id]);

    const fetchDetail = async (supervisorId: string) => {
        if (!user?.id) return;
        setDetailLoading(true);
        setDetail(null);
        setActivity(null);
        setActivityError("");
        void fetchActivity(supervisorId);
        try {
            const res = await authenticatedFetch(
                `${apiUrl}/api/admin/supervisors/${supervisorId}?user_id=${user.id}`
            );
            if (res.ok) {
                const d = await res.json();
                setDetail(d);
                setEditMaxSeats(d.settings.max_seats);
            } else {
                const data = await res.json();
                (window as any).showNotification?.(data.error || "Failed to load detail", "error");
            }
        } catch {
            (window as any).showNotification?.("Failed to load supervisor detail", "error");
        } finally {
            setDetailLoading(false);
        }
    };

    // -----------------------------------------------------------------------
    // Actions
    // -----------------------------------------------------------------------

    const handleOpenDetail = (sup: SupervisorSummary) => {
        setView("detail");
        fetchDetail(sup.user_id);
    };

    const handleBackToList = () => {
        setView("list");
        setDetail(null);
        setActivity(null);
        setActivityError("");
        fetchSupervisors();
    };

    const handleLookup = async () => {
        if (!lookupEmail.trim() || !user?.id) return;
        setLookupLoading(true);
        setLookupError("");
        setLookupResult(null);
        try {
            const res = await authenticatedFetch(
                `${apiUrl}/api/admin/users/lookup-by-email?email=${encodeURIComponent(lookupEmail.trim())}&user_id=${user.id}`
            );
            if (res.ok) {
                const data = await res.json();
                setLookupResult(data);
                setIsSupervisor(data.is_supervisor || false);
                setMaxSeats(data.supervisor_max_seats || 20);
            } else {
                const data = await res.json();
                setLookupError(data.error || "User not found");
            }
        } catch {
            setLookupError("Failed to look up user");
        } finally {
            setLookupLoading(false);
        }
    };

    const handleSaveRole = async () => {
        if (!lookupResult || !user?.id) return;
        setSaving(true);
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/admin/users/${lookupResult.user_id}/set-supervisor`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    admin_id: user.id,
                    is_supervisor: isSupervisor,
                    max_seats: maxSeats,
                }),
            });
            if (res.ok) {
                const data = await res.json();
                setLookupResult({
                    ...lookupResult,
                    is_supervisor: data.is_supervisor,
                    supervisor_max_seats: data.supervisor_max_seats,
                });
                (window as any).showNotification?.("Supervisor settings saved", "success");
                fetchSupervisors();
            } else {
                const data = await res.json();
                (window as any).showNotification?.(data.error || "Failed to save", "error");
            }
        } catch {
            (window as any).showNotification?.("Failed to save", "error");
        } finally {
            setSaving(false);
        }
    };

    const handleUpdateSeats = async () => {
        if (!detail || !user?.id) return;
        setEditingSupervisorSettings(true);
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/admin/users/${detail.user_id}/set-supervisor`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    admin_id: user.id,
                    is_supervisor: true,
                    max_seats: editMaxSeats,
                }),
            });
            if (res.ok) {
                (window as any).showNotification?.("Settings updated", "success");
                fetchDetail(detail.user_id);
            } else {
                const data = await res.json();
                (window as any).showNotification?.(data.error || "Failed", "error");
            }
        } catch {
            (window as any).showNotification?.("Failed to update settings", "error");
        } finally {
            setEditingSupervisorSettings(false);
        }
    };

    const handleRemoveStudent = async (supervisorId: string, studentId: string) => {
        if (!user?.id) return;
        setActionLoading(studentId);
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/admin/supervisors/${supervisorId}/remove-student`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ admin_id: user.id, student_id: studentId }),
            });
            if (res.ok) {
                (window as any).showNotification?.("Student unlinked", "success");
                fetchDetail(supervisorId);
            } else {
                const data = await res.json();
                (window as any).showNotification?.(data.error || "Failed", "error");
            }
        } catch {
            (window as any).showNotification?.("Failed to unlink student", "error");
        } finally {
            setActionLoading(null);
        }
    };

    const handleBulkUnlink = async (supervisorId: string) => {
        if (!user?.id) return;
        setActionLoading("bulk-unlink");
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/admin/supervisors/${supervisorId}/bulk-unlink`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ admin_id: user.id }),
            });
            if (res.ok) {
                const data = await res.json();
                (window as any).showNotification?.(
                    `${data.unlinked_count} student(s) unlinked`, "success"
                );
                fetchDetail(supervisorId);
            } else {
                const data = await res.json();
                (window as any).showNotification?.(data.error || "Failed", "error");
            }
        } catch {
            (window as any).showNotification?.("Failed to bulk unlink", "error");
        } finally {
            setActionLoading(null);
            setConfirmAction(null);
        }
    };

    const handleCancelAllInvitations = async (supervisorId: string) => {
        if (!user?.id) return;
        setActionLoading("cancel-inv");
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/admin/supervisors/${supervisorId}/cancel-invitations`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ admin_id: user.id }),
            });
            if (res.ok) {
                const data = await res.json();
                (window as any).showNotification?.(
                    `${data.cancelled_count} invitation(s) cancelled`, "success"
                );
                fetchDetail(supervisorId);
            } else {
                const data = await res.json();
                (window as any).showNotification?.(data.error || "Failed", "error");
            }
        } catch {
            (window as any).showNotification?.("Failed to cancel invitations", "error");
        } finally {
            setActionLoading(null);
            setConfirmAction(null);
        }
    };

    const handleRevokeSupervisor = async (supervisorId: string) => {
        if (!user?.id) return;
        setActionLoading("revoke");
        try {
            // First bulk-unlink
            await authenticatedFetch(`${apiUrl}/api/admin/supervisors/${supervisorId}/bulk-unlink`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ admin_id: user.id }),
            });
            // Cancel invitations
            await authenticatedFetch(`${apiUrl}/api/admin/supervisors/${supervisorId}/cancel-invitations`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ admin_id: user.id }),
            });
            // Revoke role
            const res = await authenticatedFetch(`${apiUrl}/api/admin/users/${supervisorId}/set-supervisor`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ admin_id: user.id, is_supervisor: false, max_seats: 0 }),
            });
            if (res.ok) {
                (window as any).showNotification?.("Supervisor role revoked", "success");
                handleBackToList();
            } else {
                (window as any).showNotification?.("Failed to revoke role", "error");
            }
        } catch {
            (window as any).showNotification?.("Failed to revoke supervisor", "error");
        } finally {
            setActionLoading(null);
            setConfirmAction(null);
        }
    };

    // -----------------------------------------------------------------------
    // Helpers
    // -----------------------------------------------------------------------

    const formatDate = (d: string | null | undefined) => {
        if (!d) return "-";
        try {
            return new Date(d).toLocaleDateString("en-US", {
                month: "short", day: "numeric", year: "numeric",
            });
        } catch { return "-"; }
    };

    const formatNumber = (value: number | null | undefined) => {
        return (value ?? 0).toLocaleString("en-US");
    };

    const formatPercent = (value: number | null | undefined) => {
        return `${value ?? 0}%`;
    };

    const renderMetricCard = (label: string, value: string, detailText?: string) => (
        <div style={{
            border: "1px solid var(--Colors-Neutral-200)",
            borderRadius: "10px",
            padding: "0.875rem 1rem",
            background: "var(--theme-surface-soft, var(--Colors-Neutral-101))",
            minWidth: 0,
        }}>
            <div style={{ fontSize: "0.75rem", color: "var(--Colors-Neutral-500)", marginBottom: "0.25rem" }}>
                {label}
            </div>
            <div style={{ fontSize: "1.375rem", fontWeight: 700, color: "var(--Colors-Primary-800)", lineHeight: 1.1 }}>
                {value}
            </div>
            {detailText && (
                <div style={{ fontSize: "0.75rem", color: "var(--Colors-Neutral-500)", marginTop: "0.25rem" }}>
                    {detailText}
                </div>
            )}
        </div>
    );

    // -----------------------------------------------------------------------
    // Render: List view
    // -----------------------------------------------------------------------

    const renderList = () => (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
                <h2 style={{ fontSize: "1.5rem", fontWeight: 700, color: "var(--Colors-Primary-800)", margin: 0 }}>
                    Supervisors ({supervisors.length})
                </h2>
                <button style={primaryBtn} onClick={() => { setView("promote"); setLookupResult(null); setLookupEmail(""); setLookupError(""); }}>
                    + Add Supervisor
                </button>
            </div>

            {listLoading ? (
                <div style={{ textAlign: "center", padding: "3rem", color: "var(--Colors-Neutral-500)" }}>Loading...</div>
            ) : supervisors.length === 0 ? (
                <div style={{ ...card, textAlign: "center", padding: "3rem" }}>
                    <p style={{ color: "var(--Colors-Neutral-500)" }}>No supervisors configured yet.</p>
                </div>
            ) : (
                <div style={{ ...card, padding: 0, overflow: "hidden" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                            <tr>
                                <th style={th}>Supervisor</th>
                                <th style={th}>Email</th>
                                <th style={th}>Students</th>
                                <th style={th}>Pending</th>
                                <th style={th}>Seats</th>
                                <th style={th}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {supervisors.map((s) => (
                                <tr
                                    key={s.user_id}
                                    style={{ cursor: "pointer" }}
                                    onClick={() => handleOpenDetail(s)}
                                    onMouseEnter={(e) => (e.currentTarget.style.background = "var(--theme-primary-tint)")}
                                    onMouseLeave={(e) => (e.currentTarget.style.background = "")}
                                >
                                    <td style={td}><strong>{s.full_name || "Unnamed"}</strong></td>
                                    <td style={td}>{s.email}</td>
                                    <td style={td}>{s.active_students}</td>
                                    <td style={td}>{s.pending_invitations}</td>
                                    <td style={td}>
                                        <span style={{
                                            fontWeight: 600,
                                            color: s.seats_used >= s.max_seats ? "var(--theme-danger-text)" : "var(--Colors-Primary-700)",
                                        }}>
                                            {s.seats_used}/{s.max_seats}
                                        </span>
                                    </td>
                                    <td style={td}>
                                        <button
                                            style={{ ...ghostBtn, padding: "0.375rem 0.75rem", fontSize: "0.8125rem" }}
                                            onClick={(e) => { e.stopPropagation(); handleOpenDetail(s); }}
                                        >
                                            View
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );

    // -----------------------------------------------------------------------
    // Render: Detail view
    // -----------------------------------------------------------------------

    const renderDetail = () => {
        if (detailLoading || !detail) {
            return (
                <div style={{ textAlign: "center", padding: "3rem", color: "var(--Colors-Neutral-500)" }}>
                    Loading supervisor details...
                </div>
            );
        }

        const activeStudents = detail.students.filter((s) => s.status === "active");
        const unlinkedStudents = detail.students.filter((s) => s.status === "unlinked");
        const pendingInv = detail.invitations.filter((i) => i.status === "pending");
        const activityByStudentId = new Map(
            (activity?.students || []).map((student) => [student.student_id, student])
        );

        const renderActivityOverview = () => {
            if (activityLoading && !activity) {
                return (
                    <div style={card}>
                        <h3 style={{ fontSize: "1rem", fontWeight: 600, color: "var(--Colors-Primary-800)", margin: "0 0 0.5rem" }}>
                            Last 7 Days
                        </h3>
                        <p style={{ fontSize: "0.875rem", color: "var(--Colors-Neutral-500)", margin: 0 }}>
                            Loading student activity...
                        </p>
                    </div>
                );
            }

            if (activityError) {
                return (
                    <div style={card}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
                            <div>
                                <h3 style={{ fontSize: "1rem", fontWeight: 600, color: "var(--Colors-Primary-800)", margin: "0 0 0.5rem" }}>
                                    Last 7 Days
                                </h3>
                                <p style={{ fontSize: "0.875rem", color: "var(--theme-danger-text)", margin: 0 }}>
                                    {activityError}
                                </p>
                            </div>
                            <button style={ghostBtn} onClick={() => fetchActivity(detail.user_id)}>
                                Retry
                            </button>
                        </div>
                    </div>
                );
            }

            if (!activity) {
                return (
                    <div style={card}>
                        <h3 style={{ fontSize: "1rem", fontWeight: 600, color: "var(--Colors-Primary-800)", margin: "0 0 0.5rem" }}>
                            Last 7 Days
                        </h3>
                        <p style={{ fontSize: "0.875rem", color: "var(--Colors-Neutral-500)", margin: 0 }}>
                            No activity overview loaded.
                        </p>
                    </div>
                );
            }

            const renderSummarySection = (
                title: string,
                description: string,
                summary: SupervisorActivitySummary,
            ) => (
                <div style={card}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "1rem", flexWrap: "wrap", marginBottom: "1rem" }}>
                        <div>
                            <h3 style={{ fontSize: "1rem", fontWeight: 600, color: "var(--Colors-Primary-800)", margin: 0 }}>
                                {title}
                            </h3>
                            <p style={{ fontSize: "0.8125rem", color: "var(--Colors-Neutral-500)", margin: "0.25rem 0 0" }}>
                                {description}
                            </p>
                        </div>
                        {activityLoading && (
                            <span style={{ fontSize: "0.8125rem", color: "var(--Colors-Neutral-500)" }}>
                                Refreshing...
                            </span>
                        )}
                    </div>
                    {summary.linked_students === 0 ? (
                        <p style={{ fontSize: "0.875rem", color: "var(--Colors-Neutral-500)", margin: 0 }}>
                            No active students are linked to this supervisor.
                        </p>
                    ) : (
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                            gap: "0.75rem",
                        }}>
                            {renderMetricCard("Students With Activity", `${formatNumber(summary.students_active)}/${formatNumber(summary.linked_students)}`, `${formatNumber(summary.students_inactive)} without activity`)}
                            {renderMetricCard("AI Interactions", formatNumber(summary.ai_interactions), `Chat ${formatNumber(summary.chat_messages)} / Q&A ${formatNumber(summary.direct_questions)}`)}
                            {renderMetricCard("QBank Reveals", formatNumber(summary.qbank_reveals))}
                            {renderMetricCard("Mock Exams", formatNumber(summary.mock_exams_completed))}
                            {renderMetricCard("Avg Mock Score", formatPercent(summary.avg_mock_score_pct))}
                        </div>
                    )}
                </div>
            );

            return (
                <>
                    {renderSummarySection(
                        "Last 7 Days",
                        `${formatDate(activity.period_start)} to ${formatDate(activity.period_end)}`,
                        activity.summary,
                    )}
                    {renderSummarySection(
                        "Since Linked",
                        `All study activity from each student's current join date through ${formatDate(activity.since_linked.period_end)}.`,
                        activity.since_linked.summary,
                    )}
                </>
            );
        };

        return (
            <div>
                {/* Back button */}
                <button
                    onClick={handleBackToList}
                    style={{
                        background: "none", border: "none", color: "var(--Colors-Primary-700)",
                        fontSize: "0.875rem", fontWeight: 500, cursor: "pointer", padding: 0,
                        marginBottom: "1.5rem",
                    }}
                >
                    &larr; Back to Supervisors
                </button>

                {/* Header */}
                <div style={{ ...card, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "1rem" }}>
                    <div>
                        <h2 style={{ fontSize: "1.375rem", fontWeight: 700, color: "var(--Colors-Primary-800)", margin: 0 }}>
                            {detail.profile.full_name || "Unnamed Supervisor"}
                        </h2>
                        <p style={{ fontSize: "0.875rem", color: "var(--Colors-Neutral-500)", margin: "0.25rem 0 0" }}>
                            {detail.profile.email} &middot; ID: {detail.user_id}
                        </p>
                    </div>
                    <button
                        style={{ ...dangerGhostBtn, fontSize: "0.8125rem" }}
                        onClick={() => setConfirmAction({
                            title: "Revoke Supervisor Role",
                            message: `This will unlink all ${activeStudents.length} active student(s), cancel ${pendingInv.length} pending invitation(s), revoke their supervisor-granted subscriptions, and remove the supervisor role. This cannot be undone.`,
                            action: () => handleRevokeSupervisor(detail.user_id),
                        })}
                    >
                        Revoke Supervisor Role
                    </button>
                </div>

                {/* Settings */}
                <div style={card}>
                    <h3 style={{ fontSize: "1rem", fontWeight: 600, color: "var(--Colors-Primary-800)", margin: "0 0 1rem" }}>
                        Settings
                    </h3>
                    <div style={{ display: "flex", gap: "2rem", alignItems: "flex-end", flexWrap: "wrap" }}>
                        <div>
                            <label style={{ fontSize: "0.8125rem", color: "var(--Colors-Neutral-500)", display: "block", marginBottom: "0.25rem" }}>
                                Max Seats
                            </label>
                            <input
                                type="number"
                                value={editMaxSeats}
                                onChange={(e) => setEditMaxSeats(parseInt(e.target.value) || 0)}
                                min={0}
                                style={{
                                    width: "100px", padding: "0.5rem 0.75rem",
                                    border: "1px solid var(--Colors-Neutral-200)", borderRadius: "8px",
                                    fontSize: "0.875rem",
                                }}
                            />
                        </div>
                        <div style={{ fontSize: "0.875rem", color: "var(--Colors-Neutral-600)" }}>
                            Seats used: <strong style={{ color: "var(--Colors-Primary-700)" }}>
                                {detail.seats.used}/{detail.seats.max}
                            </strong>
                            &nbsp;({detail.seats.active_students} students, {detail.seats.pending_invitations} pending)
                        </div>
                        <button
                            style={{ ...primaryBtn, padding: "0.5rem 1rem" }}
                            onClick={handleUpdateSeats}
                            disabled={editingSupervisorSettings || editMaxSeats === detail.settings.max_seats}
                        >
                            {editingSupervisorSettings ? "Saving..." : "Update Seats"}
                        </button>
                    </div>
                </div>

                {renderActivityOverview()}

                {/* Active Students */}
                <div style={{ ...card, padding: 0, overflow: "hidden" }}>
                    <div style={{
                        padding: "1rem 1.25rem", borderBottom: "1px solid var(--Colors-Neutral-200)",
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                    }}>
                        <h3 style={{ fontSize: "1rem", fontWeight: 600, color: "var(--Colors-Primary-800)", margin: 0 }}>
                            Active Students ({activeStudents.length})
                        </h3>
                        {activeStudents.length > 0 && (
                            <button
                                style={{ ...dangerGhostBtn, fontSize: "0.75rem", padding: "0.375rem 0.75rem" }}
                                disabled={actionLoading === "bulk-unlink"}
                                onClick={() => setConfirmAction({
                                    title: "Unlink All Students",
                                    message: `This will unlink all ${activeStudents.length} active student(s) and revoke any supervisor-granted subscriptions.`,
                                    action: () => handleBulkUnlink(detail.user_id),
                                })}
                            >
                                Unlink All
                            </button>
                        )}
                    </div>
                    {activeStudents.length === 0 ? (
                        <div style={{ padding: "2rem", textAlign: "center", color: "var(--Colors-Neutral-500)", fontSize: "0.875rem" }}>
                            No active students.
                        </div>
                    ) : (
                        <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", minWidth: "1180px", borderCollapse: "collapse" }}>
                            <thead>
                                <tr>
                                    <th style={th}>Student</th>
                                    <th style={th}>Email</th>
                                    <th style={th}>Last Activity</th>
                                    <th style={th}>AI Activity</th>
                                    <th style={th}>QBank</th>
                                    <th style={th}>Mock Exams</th>
                                    <th style={th}>Days Active</th>
                                    <th style={th}>Sub Granted</th>
                                    <th style={th}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {activeStudents.map((s) => {
                                    const rowActivity = activityByStudentId.get(s.student_id);
                                    const sinceLinked = rowActivity?.since_linked;
                                    const qbankProgress = rowActivity?.qbank_progress_pct ?? 0;
                                    return (
                                        <tr key={s.student_id}>
                                            <td style={td}>
                                                <strong>{s.full_name || "Unnamed"}</strong>
                                                <div style={{ fontSize: "0.75rem", color: "var(--Colors-Neutral-500)", marginTop: "0.2rem" }}>
                                                    Joined {formatDate(s.joined_at)}
                                                </div>
                                            </td>
                                            <td style={td}>{s.email}</td>
                                            <td style={td}>
                                                <div>{sinceLinked ? formatDate(sinceLinked.last_active_at) : activityLoading ? "..." : "-"}</div>
                                                {rowActivity && (
                                                    <span style={{
                                                        ...badge(rowActivity.active_this_week ? "active" : "cancelled"),
                                                        marginTop: "0.35rem",
                                                    }}>
                                                        {rowActivity.active_this_week ? "Active in last 7d" : "No activity in last 7d"}
                                                    </span>
                                                )}
                                            </td>
                                            <td style={td}>
                                                <strong>{rowActivity ? `${formatNumber(rowActivity.ai_interactions_this_week)} in 7d` : activityLoading ? "..." : "0 in 7d"}</strong>
                                                {rowActivity && (
                                                    <div style={{ fontSize: "0.75rem", color: "var(--Colors-Neutral-500)", marginTop: "0.2rem" }}>
                                                        Chat {formatNumber(rowActivity.chat_messages_this_week)} / Q&A {formatNumber(rowActivity.direct_questions_this_week)}
                                                    </div>
                                                )}
                                                <div style={{ fontSize: "0.8125rem", color: "var(--Colors-Primary-700)", marginTop: "0.45rem" }}>
                                                    {formatNumber(sinceLinked?.ai_interactions)} since linked
                                                </div>
                                                {sinceLinked && (
                                                    <div style={{ fontSize: "0.75rem", color: "var(--Colors-Neutral-500)", marginTop: "0.15rem" }}>
                                                        Chat {formatNumber(sinceLinked.chat_messages)} / Q&A {formatNumber(sinceLinked.direct_questions)}
                                                    </div>
                                                )}
                                            </td>
                                            <td style={td}>
                                                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                                    <div style={{
                                                        width: "70px",
                                                        height: "6px",
                                                        borderRadius: "999px",
                                                        background: "var(--Colors-Neutral-200)",
                                                        overflow: "hidden",
                                                    }}>
                                                        <div style={{
                                                            width: `${Math.min(Math.max(qbankProgress, 0), 100)}%`,
                                                            height: "100%",
                                                            background: "var(--Colors-Primary-600)",
                                                        }} />
                                                    </div>
                                                    <span>{formatPercent(qbankProgress)}</span>
                                                </div>
                                                <div style={{ fontSize: "0.75rem", color: "var(--Colors-Neutral-500)", marginTop: "0.45rem" }}>
                                                    {formatNumber(rowActivity?.qbank_reveals_this_week)} in 7d &middot; {formatNumber(sinceLinked?.qbank_reveals)} since linked
                                                </div>
                                            </td>
                                            <td style={td}>
                                                <strong>{rowActivity ? `${formatNumber(rowActivity.mock_exams_completed_this_week)} in 7d` : activityLoading ? "..." : "0 in 7d"}</strong>
                                                {rowActivity && rowActivity.mock_exams_completed_this_week > 0 && (
                                                    <div style={{ fontSize: "0.75rem", color: "var(--Colors-Neutral-500)", marginTop: "0.2rem" }}>
                                                        Avg {formatPercent(rowActivity.avg_mock_score_pct_this_week)}
                                                    </div>
                                                )}
                                                <div style={{ fontSize: "0.8125rem", color: "var(--Colors-Primary-700)", marginTop: "0.45rem" }}>
                                                    {formatNumber(sinceLinked?.mock_exams_completed)} since linked
                                                </div>
                                                {sinceLinked && sinceLinked.mock_exams_completed > 0 && (
                                                    <div style={{ fontSize: "0.75rem", color: "var(--Colors-Neutral-500)", marginTop: "0.15rem" }}>
                                                        Avg {formatPercent(sinceLinked.avg_mock_score_pct)}
                                                    </div>
                                                )}
                                            </td>
                                            <td style={td}>
                                                <strong>{rowActivity ? `${formatNumber(rowActivity.days_active_this_week)} in 7d` : activityLoading ? "..." : "0 in 7d"}</strong>
                                                <div style={{ fontSize: "0.8125rem", color: "var(--Colors-Primary-700)", marginTop: "0.45rem" }}>
                                                    {formatNumber(sinceLinked?.days_active)} since linked
                                                </div>
                                            </td>
                                            <td style={td}>
                                                <span style={badge(s.has_supervisor_granted_subscription ? "active" : "cancelled")}>
                                                    {s.has_supervisor_granted_subscription ? "Yes" : "No"}
                                                </span>
                                            </td>
                                            <td style={td}>
                                                <button
                                                    style={{ ...dangerGhostBtn, fontSize: "0.75rem", padding: "0.375rem 0.75rem" }}
                                                    disabled={actionLoading === s.student_id}
                                                    onClick={() => setConfirmAction({
                                                        title: "Unlink Student",
                                                        message: `Unlink ${s.full_name || s.email} from this supervisor? ${s.has_supervisor_granted_subscription ? "Their supervisor-granted subscription will be revoked." : ""}`,
                                                        action: async () => {
                                                            await handleRemoveStudent(detail.user_id, s.student_id);
                                                            setConfirmAction(null);
                                                        },
                                                    })}
                                                >
                                                    {actionLoading === s.student_id ? "..." : "Unlink"}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        </div>
                    )}
                </div>

                {/* Invitations */}
                <div style={{ ...card, padding: 0, overflow: "hidden" }}>
                    <div style={{
                        padding: "1rem 1.25rem", borderBottom: "1px solid var(--Colors-Neutral-200)",
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                    }}>
                        <h3 style={{ fontSize: "1rem", fontWeight: 600, color: "var(--Colors-Primary-800)", margin: 0 }}>
                            Invitations ({detail.invitations.length})
                        </h3>
                        {pendingInv.length > 0 && (
                            <button
                                style={{ ...dangerGhostBtn, fontSize: "0.75rem", padding: "0.375rem 0.75rem" }}
                                disabled={actionLoading === "cancel-inv"}
                                onClick={() => setConfirmAction({
                                    title: "Cancel All Pending Invitations",
                                    message: `This will cancel ${pendingInv.length} pending invitation(s).`,
                                    action: () => handleCancelAllInvitations(detail.user_id),
                                })}
                            >
                                Cancel All Pending
                            </button>
                        )}
                    </div>
                    {detail.invitations.length === 0 ? (
                        <div style={{ padding: "2rem", textAlign: "center", color: "var(--Colors-Neutral-500)", fontSize: "0.875rem" }}>
                            No invitations.
                        </div>
                    ) : (
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr>
                                    <th style={th}>Email</th>
                                    <th style={th}>Status</th>
                                    <th style={th}>Sent</th>
                                    <th style={th}>Accepted / Expires</th>
                                </tr>
                            </thead>
                            <tbody>
                                {detail.invitations.map((inv) => (
                                    <tr key={inv.id}>
                                        <td style={td}>{inv.email}</td>
                                        <td style={td}><span style={badge(inv.status)}>{inv.status}</span></td>
                                        <td style={td}>{formatDate(inv.invited_at)}</td>
                                        <td style={td}>
                                            {inv.status === "accepted"
                                                ? formatDate(inv.accepted_at)
                                                : inv.status === "pending"
                                                    ? `Expires ${formatDate(inv.expires_at)}`
                                                    : "-"
                                            }
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Unlinked Students (history) */}
                {unlinkedStudents.length > 0 && (
                    <div style={{ ...card, padding: 0, overflow: "hidden" }}>
                        <div style={{ padding: "1rem 1.25rem", borderBottom: "1px solid var(--Colors-Neutral-200)" }}>
                            <h3 style={{ fontSize: "1rem", fontWeight: 600, color: "var(--Colors-Neutral-500)", margin: 0 }}>
                                Unlinked Students ({unlinkedStudents.length})
                            </h3>
                        </div>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr>
                                    <th style={th}>Student</th>
                                    <th style={th}>Email</th>
                                    <th style={th}>Joined</th>
                                    <th style={th}>Unlinked</th>
                                </tr>
                            </thead>
                            <tbody>
                                {unlinkedStudents.map((s) => (
                                    <tr key={s.student_id}>
                                        <td style={{ ...td, color: "var(--Colors-Neutral-500)" }}>{s.full_name || "Unnamed"}</td>
                                        <td style={{ ...td, color: "var(--Colors-Neutral-500)" }}>{s.email}</td>
                                        <td style={{ ...td, color: "var(--Colors-Neutral-500)" }}>{formatDate(s.joined_at)}</td>
                                        <td style={{ ...td, color: "var(--Colors-Neutral-500)" }}>{formatDate(s.unlinked_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    };

    // -----------------------------------------------------------------------
    // Render: Promote view
    // -----------------------------------------------------------------------

    const renderPromote = () => (
        <div>
            <button
                onClick={handleBackToList}
                style={{
                    background: "none", border: "none", color: "var(--Colors-Primary-700)",
                    fontSize: "0.875rem", fontWeight: 500, cursor: "pointer", padding: 0,
                    marginBottom: "1.5rem",
                }}
            >
                &larr; Back to Supervisors
            </button>

            <h2 style={{ fontSize: "1.5rem", fontWeight: 700, color: "var(--Colors-Primary-800)", marginBottom: "1.5rem" }}>
                Add Supervisor
            </h2>

            {/* Lookup */}
            <div style={card}>
                <h3 style={{ fontSize: "1rem", fontWeight: 600, color: "var(--Colors-Primary-800)", marginBottom: "0.75rem" }}>
                    Look Up User by Email
                </h3>
                <div style={{ display: "flex", gap: "0.75rem", alignItems: "flex-start" }}>
                    <input
                        type="email"
                        placeholder="Enter user email..."
                        value={lookupEmail}
                        onChange={(e) => setLookupEmail(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") handleLookup(); }}
                        style={{
                            flex: 1, padding: "0.625rem 1rem",
                            border: "1px solid var(--Colors-Neutral-200)", borderRadius: "10px",
                            fontSize: "0.875rem", outline: "none",
                        }}
                    />
                    <button
                        onClick={handleLookup}
                        disabled={lookupLoading || !lookupEmail.trim()}
                        style={{ ...primaryBtn, opacity: (lookupLoading || !lookupEmail.trim()) ? 0.5 : 1 }}
                    >
                        {lookupLoading ? "Looking up..." : "Look Up"}
                    </button>
                </div>
                {lookupError && (
                    <p style={{ color: "var(--theme-danger-text)", fontSize: "0.875rem", marginTop: "0.5rem" }}>{lookupError}</p>
                )}
            </div>

            {/* Result */}
            {lookupResult && (
                <div style={card}>
                    <h3 style={{ fontSize: "1rem", fontWeight: 600, color: "var(--Colors-Primary-800)", marginBottom: "0.25rem" }}>
                        {lookupResult.full_name || "Unnamed User"}
                    </h3>
                    <p style={{ fontSize: "0.875rem", color: "var(--Colors-Neutral-500)", marginBottom: "1rem" }}>
                        {lookupResult.email} &middot; ID: {lookupResult.user_id}
                        {lookupResult.is_supervisor && (
                            <span style={{ ...badge("active"), marginLeft: "0.5rem" }}>Already Supervisor</span>
                        )}
                    </p>

                    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                        <label style={{ display: "flex", alignItems: "center", gap: "0.75rem", cursor: "pointer" }}>
                            <input
                                type="checkbox"
                                checked={isSupervisor}
                                onChange={(e) => setIsSupervisor(e.target.checked)}
                                style={{ width: "18px", height: "18px" }}
                            />
                            <span style={{ fontSize: "0.875rem", fontWeight: 500 }}>Supervisor Role</span>
                        </label>

                        {isSupervisor && (
                            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                                <label style={{ fontSize: "0.875rem", fontWeight: 500, minWidth: "80px" }}>Max Seats:</label>
                                <input
                                    type="number"
                                    value={maxSeats}
                                    onChange={(e) => setMaxSeats(parseInt(e.target.value) || 0)}
                                    min={0}
                                    style={{
                                        width: "100px", padding: "0.5rem 0.75rem",
                                        border: "1px solid var(--Colors-Neutral-200)", borderRadius: "8px",
                                        fontSize: "0.875rem",
                                    }}
                                />
                            </div>
                        )}

                        <button
                            onClick={handleSaveRole}
                            disabled={saving}
                            style={{ ...primaryBtn, width: "fit-content", opacity: saving ? 0.5 : 1 }}
                        >
                            {saving ? "Saving..." : "Save"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );

    // -----------------------------------------------------------------------
    // Confirmation modal
    // -----------------------------------------------------------------------

    const renderConfirmModal = () => {
        if (!confirmAction) return null;
        return (
            <div
                style={{
                    position: "fixed", inset: 0, background: "var(--theme-overlay, rgba(0,0,0,0.5))",
                    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
                }}
                onClick={() => setConfirmAction(null)}
            >
                <div
                    style={{
                        background: "var(--theme-surface-strong, white)", borderRadius: "16px", padding: "2rem",
                        border: "1px solid var(--theme-border-strong)",
                        maxWidth: "440px", width: "90%", boxShadow: "0 8px 40px var(--theme-shadow, rgba(0,0,0,0.15))",
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <h3 style={{ fontSize: "1.125rem", fontWeight: 600, color: "var(--Colors-Primary-800)", margin: "0 0 0.75rem" }}>
                        {confirmAction.title}
                    </h3>
                    <p style={{ fontSize: "0.875rem", color: "var(--Colors-Neutral-600)", lineHeight: 1.5, margin: "0 0 1.5rem" }}>
                        {confirmAction.message}
                    </p>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem" }}>
                        <button style={ghostBtn} onClick={() => setConfirmAction(null)}>Cancel</button>
                        <button
                            style={{ ...dangerBtn, opacity: actionLoading ? 0.5 : 1 }}
                            disabled={!!actionLoading}
                            onClick={confirmAction.action}
                        >
                            {actionLoading ? "Processing..." : "Confirm"}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // -----------------------------------------------------------------------
    // Main render
    // -----------------------------------------------------------------------

    return (
        <div className={styles.pageShell}>
            {view === "list" && renderList()}
            {view === "detail" && renderDetail()}
            {view === "promote" && renderPromote()}
            {renderConfirmModal()}
        </div>
    );
};

export default AdminSupervisors;
